/**
 * EN-837 — pure decision function extracted from app.tsx's `onAuthStateChange` session-null
 * handler so the branching can be unit-tested without a full router/Supabase.
 *
 * The effect maps auth state to one of these actions, then executes it via
 * `runWhenRecorderIdle` (see recorderActivity.ts) so it defers while a recording is active.
 */

import { ENDPOINTS } from "@/common/utils/url-registry";

export type SessionClearedAction =
    | { type: "noop" }
    | { type: "hardNav"; href: string }
    | { type: "navigateReplace"; to: string };

export interface SessionClearedParams {
    /** window.location.pathname at the time the session cleared. */
    pathname: string;
    /** Supabase auth error_code from the URL (query or hash), or null. */
    errorCode: string | null;
    /** Supabase auth error_description from the URL, or null. */
    errorDescription: string | null;
    /** Whether the device lock (PIN) is engaged — a deliberate sign-out we must not fight. */
    isLocked: boolean;
}

/**
 * Decide what to do when the Supabase session becomes null (event !== "INITIAL_SESSION").
 *
 * Branch order preserves the original app.tsx logic (now extracted here; app.tsx calls this):
 *  1. AcceptInvite route → noop (updateUser during invite onboarding briefly clears the session).
 *  2. otp_expired / invalid email link → hardNav to /reset-password?expired=1 (recovery) or
 *     /resend-verification-email (signup/verification). NOTE: this is checked BEFORE the lock
 *     check here (step 2 precedes step 3 below), so it wins even when the device is locked.
 *  3. Device locked → noop (lock intentionally signs out; reloading would flash the lock UI).
 *  4. otherwise → navigateReplace to "/" (swap to unauthenticated routes).
 */
export function resolveSessionClearedAction(params: SessionClearedParams): SessionClearedAction {
    const { pathname, errorCode, errorDescription, isLocked } = params;

    if (pathname.startsWith(`/${ENDPOINTS.AcceptInvite}`)) {
        return { type: "noop" };
    }

    const isOtpExpired = errorCode === "otp_expired";
    const isInvalidEmailLink = errorCode === "403" && !!errorDescription?.startsWith("Email link");
    if (isOtpExpired || isInvalidEmailLink) {
        // Recovery (password-reset) links use redirectTo=/update-password, so Supabase's
        // implicit-flow error redirect preserves that pathname on expiry. A confirmed user who
        // only did a password reset must NOT be sent to the verification-resend page (a no-op
        // loop for them) — route them to the reset-request page with an expiry banner instead.
        // Signup/verification links land on /email-verified and keep the resend behavior.
        const isRecoveryFlow = pathname.startsWith(`/${ENDPOINTS.UpdatePassword}`);
        return {
            type: "hardNav",
            href: isRecoveryFlow ? `/${ENDPOINTS.ResetPassword}?expired=1` : "/resend-verification-email",
        };
    }

    if (isLocked) {
        return { type: "noop" };
    }

    return { type: "navigateReplace", to: "/" };
}
